import { AlertCircle } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { ERROR_CORRECTION_OPTIONS, QR_CODE_SIZES } from '../config/styles';
import { describeQrError, MAX_CONTENT_LENGTH, type QrGenerationResult } from '../domain/generator';
import { ERROR_CORRECTION_LEVELS, type ErrorCorrectionLevel } from '../domain/types';
import { useQrContext } from '../state/qr-context';

function isErrorCorrectionLevel(value: string): value is ErrorCorrectionLevel {
  return (ERROR_CORRECTION_LEVELS as readonly string[]).includes(value);
}

export function BasicSettings({ result }: { readonly result: QrGenerationResult }) {
  const { design, actions } = useQrContext();
  const remaining = MAX_CONTENT_LENGTH - design.content.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>QR-Code Einstellungen</CardTitle>
        <CardDescription>Inhalt, Fehlerkorrektur und Größe</CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-baseline justify-between">
            <Label htmlFor="qr-content">Inhalt</Label>
            <span
              className={
                remaining < 0
                  ? 'text-xs font-medium text-destructive'
                  : 'text-xs text-muted-foreground'
              }
            >
              {design.content.length} / {MAX_CONTENT_LENGTH}
            </span>
          </div>
          <Input
            id="qr-content"
            value={design.content}
            aria-invalid={!result.ok}
            aria-describedby={result.ok ? undefined : 'qr-content-error'}
            placeholder="URL oder Text eingeben…"
            onChange={(event) => {
              actions.setContent(event.target.value);
            }}
          />
          {!result.ok && (
            <Alert variant="destructive" id="qr-content-error">
              <AlertCircle aria-hidden="true" />
              <AlertDescription>{describeQrError(result.error)}</AlertDescription>
            </Alert>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="qr-ecl">Fehlerkorrektur</Label>
          <Select
            value={design.errorCorrectionLevel}
            onValueChange={(value) => {
              if (isErrorCorrectionLevel(value)) actions.setErrorCorrectionLevel(value);
            }}
          >
            <SelectTrigger id="qr-ecl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ERROR_CORRECTION_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label} — {option.description}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="qr-size">Größe</Label>
          <Select
            value={String(design.size)}
            onValueChange={(value) => {
              const parsed = Number.parseInt(value, 10);
              if (Number.isFinite(parsed)) actions.setSize(parsed);
            }}
          >
            <SelectTrigger id="qr-size">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {QR_CODE_SIZES.map((size) => (
                <SelectItem key={size.value} value={String(size.value)}>
                  {size.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
}
